import { useEffect, useState } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { CROPS, cropById } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Sprout, Plus, Trash2, Sparkles, CalendarClock, Scale, Coins } from "lucide-react";
import { toast } from "sonner";

const blank = () => {
  const c = CROPS[0];
  return {
    crop: c.id, planted_count: 100, plant_date: new Date().toISOString().slice(0, 10),
    grow_days: c.grow_days, yield_per_plant: c.yield_per_plant, unit: c.unit, price_per_unit: c.price_per_unit,
    seed_cost: 0, fertilizer_cost: 0, labor_cost: 0, other_cost: 0, notes: "", custom_name: "",
  };
};

function daysLeft(harvest) {
  if (!harvest) return null;
  const d = Math.ceil((new Date(harvest) - new Date()) / 86400000);
  return d;
}

export default function Crops() {
  const { fmt, settings } = useFarm();
  const [items, setItems] = useState([]);
  const [savedCrops, setSavedCrops] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const load = () => api.getPlantings().then(setItems);
  const loadSaved = () => api.getCustomCrops().then(setSavedCrops);
  useEffect(() => { load(); loadSaved(); }, []);

  const pickCrop = (id) => {
    if (id === "__custom__") {
      setForm((f) => ({ ...f, crop: "__custom__", custom_name: "", grow_days: 90, yield_per_plant: 1, unit: "kg", price_per_unit: 1 }));
      return;
    }
    if (String(id).startsWith("saved:")) {
      const c = savedCrops.find((s) => s.id === id.slice(6));
      if (c) {
        setForm((f) => ({ ...f, crop: id, custom_name: c.name, grow_days: c.grow_days, yield_per_plant: c.yield_per_plant, unit: c.unit, price_per_unit: c.price_per_unit }));
        return;
      }
    }
    const c = cropById(id);
    setForm((f) => ({ ...f, crop: c.id, custom_name: "", grow_days: c.grow_days, yield_per_plant: c.yield_per_plant, unit: c.unit, price_per_unit: c.price_per_unit }));
  };

  const expYield = (form.planted_count || 0) * (form.yield_per_plant || 0);
  const expAmount = expYield * (form.price_per_unit || 0);
  const inputCost = (form.seed_cost || 0) + (form.fertilizer_cost || 0) + (form.labor_cost || 0) + (form.other_cost || 0);
  const netProfit = expAmount - inputCost;
  const harvest = (() => {
    const d = new Date(form.plant_date);
    d.setDate(d.getDate() + Number(form.grow_days || 0));
    return isNaN(d) ? "" : d.toISOString().slice(0, 10);
  })();

  const save = async () => {
    const isCustom = form.crop === "__custom__";
    const isSaved = String(form.crop).startsWith("saved:");
    const cropName = (isCustom || isSaved) ? (form.custom_name || "").trim() : cropById(form.crop).name;
    if (!cropName || !form.planted_count) return toast.error("Crop name and number planted are required");
    await api.createPlanting({ ...form, crop: cropName });
    if (isCustom || isSaved) {
      await api.saveCustomCrop({ name: cropName, grow_days: form.grow_days, yield_per_plant: form.yield_per_plant, unit: form.unit, price_per_unit: form.price_per_unit });
      loadSaved();
    }
    toast.success("Planting recorded — expected income added");
    setOpen(false); setForm(blank()); load();
  };
  const remove = async (id) => { await api.deletePlanting(id); load(); toast.success("Removed"); };
  const harvestNow = async (id) => { await api.harvestPlanting(id); load(); toast.success("Harvested — income marked received"); };
  const removeSaved = async (id) => { await api.deleteCustomCrop(id); loadSaved(); toast.success("Removed saved crop"); };

  // Per-crop statistics (client-side)
  const stats = Object.values(items.reduce((acc, p) => {
    const k = p.crop;
    const s = acc[k] || (acc[k] = { crop: k, plantings: 0, planted: 0, expected_yield: 0, expected_amount: 0, input_cost: 0, unit: p.unit });
    s.plantings += 1; s.planted += p.planted_count; s.expected_yield += p.expected_yield; s.expected_amount += p.expected_amount; s.input_cost += p.input_cost || 0;
    return acc;
  }, {}));

  const totalExpected = items.reduce((s, p) => s + (p.status === "growing" ? p.expected_amount : 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Sprout className="h-7 w-7 text-[hsl(var(--emerald))]" /> Crops &amp; Plantings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Record plantings — we estimate harvest date and expected sale value from your yield inputs.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 rounded-full gap-1.5" onClick={() => setForm(blank())} data-testid="add-planting-button"><Plus className="h-4 w-4" /> Record Planting</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Record Planting</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Crop</Label>
                <Select value={form.crop} onValueChange={pickCrop}>
                  <SelectTrigger className="mt-1.5" data-testid="planting-crop-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CROPS.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    {savedCrops.map((c) => <SelectItem key={c.id} value={`saved:${c.id}`}>{c.name} (saved)</SelectItem>)}
                    <SelectItem value="__custom__">+ Custom crop…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.crop === "__custom__" || String(form.crop).startsWith("saved:")) && (
                <div className="col-span-2">
                  <Label>Crop Name</Label>
                  <Input className="mt-1.5" placeholder="e.g. Sweet Potato" value={form.custom_name} onChange={(e) => setForm({ ...form, custom_name: e.target.value })} data-testid="planting-custom-name-input" />
                  <p className="text-xs text-muted-foreground mt-1">We'll remember this crop and its details for next time.</p>
                </div>
              )}
              <div>
                <Label>Number Planted</Label>
                <Input type="number" className="mt-1.5" value={form.planted_count} onChange={(e) => setForm({ ...form, planted_count: parseInt(e.target.value) || 0 })} data-testid="planting-count-input" />
              </div>
              <div>
                <Label>Plant Date</Label>
                <Input type="date" className="mt-1.5" value={form.plant_date} onChange={(e) => setForm({ ...form, plant_date: e.target.value })} data-testid="planting-date-input" />
              </div>
              <div>
                <Label>Grow Duration (days)</Label>
                <Input type="number" className="mt-1.5" value={form.grow_days} onChange={(e) => setForm({ ...form, grow_days: parseInt(e.target.value) || 0 })} data-testid="planting-growdays-input" />
              </div>
              <div>
                <Label>Yield / plant ({form.unit})</Label>
                <Input type="number" step="0.01" className="mt-1.5" value={form.yield_per_plant} onChange={(e) => setForm({ ...form, yield_per_plant: parseFloat(e.target.value) || 0 })} data-testid="planting-yield-input" />
              </div>
              <div className="col-span-2">
                <Label>Sale Price per {form.unit} ({settings.currency_symbol})</Label>
                <Input type="number" step="0.01" className="mt-1.5" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: parseFloat(e.target.value) || 0 })} data-testid="planting-price-input" />
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-3 pt-1 border-t border-border">
                <div>
                  <Label className="text-xs">Seeds ({settings.currency_symbol})</Label>
                  <Input type="number" step="0.01" className="mt-1.5" value={form.seed_cost} onChange={(e) => setForm({ ...form, seed_cost: parseFloat(e.target.value) || 0 })} data-testid="planting-seed-cost-input" />
                </div>
                <div>
                  <Label className="text-xs">Fertiliser ({settings.currency_symbol})</Label>
                  <Input type="number" step="0.01" className="mt-1.5" value={form.fertilizer_cost} onChange={(e) => setForm({ ...form, fertilizer_cost: parseFloat(e.target.value) || 0 })} data-testid="planting-fertilizer-cost-input" />
                </div>
                <div>
                  <Label className="text-xs">Labour ({settings.currency_symbol})</Label>
                  <Input type="number" step="0.01" className="mt-1.5" value={form.labor_cost} onChange={(e) => setForm({ ...form, labor_cost: parseFloat(e.target.value) || 0 })} data-testid="planting-labor-cost-input" />
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-[hsl(var(--emerald))]/10 border border-[hsl(var(--emerald))]/30 p-4 space-y-2" data-testid="planting-projection">
              <p className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--emerald))]"><Sparkles className="h-4 w-4" /> Projection</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                <div><span className="block text-xs text-muted-foreground">Exp. Yield</span><span className="font-mono-nums font-bold">{expYield.toLocaleString()} {form.unit}</span></div>
                <div><span className="block text-xs text-muted-foreground">Exp. Sale</span><span className="font-mono-nums font-bold" data-testid="projection-amount">{fmt(expAmount)}</span></div>
                <div><span className="block text-xs text-muted-foreground">Input Cost</span><span className="font-mono-nums font-bold text-[hsl(var(--terracotta))]" data-testid="projection-input-cost">{fmt(inputCost)}</span></div>
                <div><span className="block text-xs text-muted-foreground">Est. Net</span><span className="font-mono-nums font-bold" data-testid="projection-net">{fmt(netProfit)}</span></div>
                <div><span className="block text-xs text-muted-foreground">Harvest ~</span><span className="font-mono-nums font-bold">{harvest}</span></div>
              </div>
            </div>
            <DialogFooter><Button className="rounded-full" onClick={save} data-testid="save-planting-button">Save Planting</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Headline stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-5"><p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Sprout className="h-3.5 w-3.5" /> Active Plantings</p><p className="mt-1 font-display text-2xl font-extrabold font-mono-nums">{items.filter((i) => i.status === "growing").length}</p></Card>
        <Card className="p-5"><p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Coins className="h-3.5 w-3.5" /> Projected Harvest Value</p><p className="mt-1 font-display text-2xl font-extrabold font-mono-nums text-[hsl(var(--emerald))]" data-testid="projected-value">{fmt(totalExpected)}</p></Card>
        <Card className="p-5"><p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Scale className="h-3.5 w-3.5" /> Crop Varieties</p><p className="mt-1 font-display text-2xl font-extrabold font-mono-nums">{stats.length}</p></Card>
      </div>

      {/* Saved custom crops */}
      {savedCrops.length > 0 && (
        <Card className="p-4" data-testid="saved-crops">
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Sprout className="h-3.5 w-3.5" /> Your Saved Crops — pick any from the Record Planting list to reuse</p>
          <div className="flex flex-wrap gap-2">
            {savedCrops.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-3 pr-1 py-1 text-sm" data-testid={`saved-crop-${c.id}`}>
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-muted-foreground font-mono-nums">{c.grow_days}d · {fmt(c.price_per_unit)}/{c.unit}</span>
                <button onClick={() => removeSaved(c.id)} className="rounded-full p-1 text-muted-foreground hover:text-destructive" data-testid={`delete-saved-crop-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Per-crop statistics */}
      {stats.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="crop-stats">
          {stats.map((s) => (
            <Card key={s.crop} className="p-4" data-testid={`crop-stat-${s.crop}`}>
              <p className="font-display font-bold">{s.crop}</p>
              <div className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-muted-foreground">Plantings</span><span className="text-right font-mono-nums">{s.plantings}</span>
                <span className="text-muted-foreground">Planted</span><span className="text-right font-mono-nums">{s.planted.toLocaleString()}</span>
                <span className="text-muted-foreground">Exp. Yield</span><span className="text-right font-mono-nums">{s.expected_yield.toLocaleString()} {s.unit}</span>
                <span className="text-muted-foreground">Exp. Sale</span><span className="text-right font-mono-nums font-semibold text-[hsl(var(--emerald))]">{fmt(s.expected_amount)}</span>
                <span className="text-muted-foreground">Input Cost</span><span className="text-right font-mono-nums text-[hsl(var(--terracotta))]">{fmt(s.input_cost)}</span>
                <span className="text-muted-foreground">Est. Net</span><span className="text-right font-mono-nums font-semibold">{fmt(s.expected_amount - s.input_cost)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Crop calendar: upcoming harvests */}
      {items.some((i) => i.status === "growing") && (
        <Card className="p-6" data-testid="crop-calendar">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-accent" /> Upcoming Harvests</h2>
          <div className="space-y-2">
            {items.filter((i) => i.status === "growing").sort((a, b) => (a.harvest_date < b.harvest_date ? -1 : 1)).map((p) => {
              const dl = daysLeft(p.harvest_date);
              const soon = dl !== null && dl <= 7;
              return (
                <div key={p.id} className={`flex flex-wrap items-center gap-3 rounded-xl border p-3.5 ${soon ? "border-amber-500/50 bg-amber-500/10" : "border-border"}`} data-testid={`calendar-row-${p.id}`}>
                  <div className="flex-1 min-w-[150px]">
                    <p className="font-semibold">{p.crop}</p>
                    <p className="text-xs text-muted-foreground font-mono-nums">Harvest {p.harvest_date} · {p.planted_count.toLocaleString()} planted</p>
                  </div>
                  <span className="font-mono-nums text-sm text-[hsl(var(--emerald))] font-semibold">{fmt(p.expected_amount)}</span>
                  {soon ? (
                    <Badge className="bg-amber-500 text-white border-0" data-testid={`reminder-${p.id}`}>{dl > 0 ? `Due in ${dl}d` : "Harvest now"}</Badge>
                  ) : (
                    <Badge variant="secondary" className="font-mono-nums">{dl}d left</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Plantings table */}
      <Card className="overflow-hidden">
        <div className="p-5 border-b border-border"><h2 className="font-display text-lg font-bold tracking-tight">Planting Log</h2></div>
        {items.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="plantings-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Crop</TableHead>
                  <TableHead className="text-right">Planted</TableHead>
                  <TableHead className="text-right">Exp. Yield</TableHead>
                  <TableHead className="text-right">Exp. Sale</TableHead>
                  <TableHead>Harvest</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => {
                  const dl = daysLeft(p.harvest_date);
                  return (
                    <TableRow key={p.id} data-testid={`planting-row-${p.id}`}>
                      <TableCell className="font-medium">{p.crop}</TableCell>
                      <TableCell className="text-right font-mono-nums">{p.planted_count.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-nums">{p.expected_yield.toLocaleString()} {p.unit}</TableCell>
                      <TableCell className="text-right font-mono-nums font-semibold text-[hsl(var(--emerald))]">{fmt(p.expected_amount)}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-mono-nums">{p.harvest_date}</span>
                        {p.status === "growing" && dl !== null && (
                          <span className="block text-xs text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3 w-3" />{dl > 0 ? `${dl} days left` : "due"}</span>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={p.status === "harvested" ? "default" : "secondary"} className="capitalize">{p.status}</Badge></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {p.status === "growing" && (
                          <Button variant="outline" size="sm" className="rounded-full mr-1 h-8" onClick={() => harvestNow(p.id)} data-testid={`harvest-${p.id}`}>Harvest</Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(p.id)} data-testid={`delete-planting-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Sprout className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">No plantings yet. Record one to project your harvest income.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
